/** @jsxImportSource @emotion/react */
import { styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { css, type CSSObject } from '@emotion/react';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { type CSSProperties, useId, useState } from 'react';

import { InfoIcon as KaiShikiInfoIcon } from '@/Client/Icons/kaiShikiIconAliases';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { SettingsToggle } from '@/Client/Widgets/Settings';
import { CommonActionButton, CommonField, CommonFieldAdornment, DropdownIcon } from '@kaiizen/kai-shiki/react';
import {
  ORG_SETTINGS_BODY_TEXT_STYLE,
  ORG_SETTINGS_COLORS,
  ORG_SETTINGS_SUBHEADING_STYLE,
} from './OrgSettingsStyleLib';

/* The Workflow tab's card, as the mockup draws it: a bordered white panel in a stack, rather
   than a full-width row with a divider under it. Head and body carry their own padding, so the
   card itself sets none (KAIDEV-4345). */
const ITEM_STYLE = css`
  label: ITEM_STYLE;
  display: flex;
  flex-direction: column;
  border: 1px solid ${ORG_SETTINGS_COLORS.border};
  background: ${ORG_SETTINGS_COLORS.white};
`;

/** A disabled card drops to the paper ground and mutes its body, keeping its toggle reachable. */
const ITEM_OFF_STYLE = css`
  label: ITEM_OFF_STYLE;
  background: ${ORG_SETTINGS_COLORS.pageBackground};
`;

const HEADER_ROW_STYLE = css`
  label: HEADER_ROW_STYLE;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px;
`;

const BODY_STYLE = css`
  label: BODY_STYLE;
  padding: 0 22px 22px;
`;

const BODY_OFF_STYLE = css`
  label: BODY_OFF_STYLE;
  opacity: 0.55;
`;

const TEXT_BLOCK_STYLE = css`
  label: TEXT_BLOCK_STYLE;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ITEM_TITLE_STYLE = css`
  label: ITEM_TITLE_STYLE;
  ${ORG_SETTINGS_SUBHEADING_STYLE}
`;

const ITEM_DESCRIPTION_STYLE = css`
  label: ITEM_DESCRIPTION_STYLE;
  ${ORG_SETTINGS_BODY_TEXT_STYLE}
`;

const DISABLED_TEXT_STYLE = css`
  label: DISABLED_TEXT_STYLE;
  color: ${ORG_SETTINGS_COLORS.textSoft};
`;

const CONTROLS_STYLE = css`
  label: CONTROLS_STYLE;
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 0 0 auto;
`;

/* The Workflow tab's cards open one at a time rather than showing every field at once, which is
   how the mockup draws both of its states (KAIDEV-4345). */
const CHEVRON_STYLE = css`
  label: CHEVRON_STYLE;
  display: flex;
  padding: 4px;
  border: 0;
  background: none;
  cursor: pointer;
  color: ${ORG_SETTINGS_COLORS.textMuted};
  transition: transform 0.16s;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CHEVRON_EXPANDED_STYLE = css`
  label: CHEVRON_EXPANDED_STYLE;
  transform: rotate(180deg);
`;

const INPUT_ROW_STYLE = css`
  label: INPUT_ROW_STYLE;
  display: grid;
  grid-template-columns: 400px minmax(0, 1fr);
  gap: 20px;
`;

const FIELD_WRAPPER_STYLE = {
  position: 'relative',
  minHeight: 53,
  '& input': {
    fontSize: `${pxToRem(FONT_SIZE.bodySmall)} !important`,
  },
} satisfies CSSObject;

const FIELD_STYLE = {
  width: '100%',
  background: 'transparent',
} satisfies CSSProperties;

const FIELD_BOX_STYLE = {
  border: `1px solid ${ORG_SETTINGS_COLORS.textFieldLabel}`,
  borderRadius: 0,
  background: ORG_SETTINGS_COLORS.white,
} satisfies CSSObject;

const FIELD_LABEL_STYLE = {
  background: ORG_SETTINGS_COLORS.pageBackground,
} satisfies CSSObject;

const FIELD_VALUE_STYLE = {
  fontFamily: FONT.body,
  fontWeight: FONT_WEIGHT.regular,
  minWidth: 0,
  paddingRight: 48,
} satisfies CSSObject;

/* Goes through Emotion's `css`, not `valueCss`: kai-shiki applies `valueCss` as an inline style
   and the DOM silently drops a pseudo-element key, so this rule never rendered while it lived
   there (KAIDEV-4398). The data attribute is kai-shiki's own hook for the value element.

   Keep it in its own object. The types only catch part of this: `StyleOverride` rejects an
   inline literal and rejects a const whose keys are ALL pseudo/selector keys (weak-type
   detection), but a const mixing one real CSS property with a pseudo key assigns to it with no
   error and is still dropped at runtime. Folding this into FIELD_VALUE_STYLE would compile and
   silently stop working. OrgSettingsIntegration.test.tsx pins that it renders. */
const FIELD_PLACEHOLDER_STYLE = {
  'input[data-kai-shiki-common-field-value]::placeholder': {
    color: ORG_SETTINGS_COLORS.textMuted,
    opacity: 1,
  },
} satisfies CSSObject;

type WorkflowProjectSettingsItemProps = {
  baseProjectTypeLabel: string;
  baseProjectTypeDescription: string;
  projectTypeLabel: string;
  projectDescription: string;
  enabled?: boolean;
  onToggleClick?: () => void;
  onProjectTypeLabelChange?: (value: string) => void;
  onProjectDescriptionChange?: (value: string) => void;
  /** Commits a pending edit without waiting out the typing pause. */
  onFieldBlur?: () => void;
  /** Marks the field as being typed in, so a refresh does not rewrite it mid-word. */
  onFieldFocus?: () => void;
};

const FIELD_COUNT_STYLE = {
  position: 'absolute',
  right: 20,
  top: 23,
  fontFamily: FONT.body,
  fontSize: pxToRem(FONT_SIZE.bodyMicro),
  fontStyle: 'normal',
  fontWeight: FONT_WEIGHT.regular,
  lineHeight: 1.6,
  letterSpacing: '0.2px',
  color: ORG_SETTINGS_COLORS.textMuted,
} satisfies CSSObject;

const FIELD_COUNT_WITH_ICON_STYLE = {
  right: 44,
  color: ORG_SETTINGS_COLORS.textFieldLabel,
} satisfies CSSObject;

const INFO_ICON_BUTTON_STYLE: CSSProperties = {
  position: 'absolute',
  right: '10px',
  top: pxToRem(16),
  width: pxToRem(24),
  height: pxToRem(24),
  border: 0,
  padding: 0,
  background: 'transparent',
  color: ORG_SETTINGS_COLORS.text,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

const INFO_ICON_BUTTON_DISABLED_STYLE: CSSProperties = {
  color: ORG_SETTINGS_COLORS.textSoft,
};

const PROJECT_LABEL_TOOLTIP_STYLE = css`
  label: PROJECT_LABEL_TOOLTIP_STYLE;
  position: absolute;
  top: 55px;
  right: -106px;
  z-index: 2;
  width: 253px;
  min-height: 78px;
  padding: 15px 17px;
  border-radius: 0;
  background: ${ORG_SETTINGS_COLORS.textMuted};
  color: ${ORG_SETTINGS_COLORS.white};
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  font-style: normal;
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.333;
  letter-spacing: 0.24px;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 119px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid ${ORG_SETTINGS_COLORS.textMuted};
  }
`;

const PROJECT_LABEL_TOOLTIP_TEXT = 'Add a custom label to change how this project displays in the system. It stays in its original category.';

const InfoIcon = () => <KaiShikiInfoIcon size={24} />;

export const WorkflowProjectSettingsItem = ({
  baseProjectTypeLabel,
  baseProjectTypeDescription,
  projectTypeLabel,
  projectDescription,
  enabled = true,
  onToggleClick,
  onProjectTypeLabelChange,
  onProjectDescriptionChange,
  onFieldBlur,
  onFieldFocus,
}: WorkflowProjectSettingsItemProps) => {
  const [showProjectLabelTooltip, setShowProjectLabelTooltip] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const bodyId = useId();
  const tooltipId = `project-type-${baseProjectTypeLabel.toLowerCase()}-label-tooltip`;

  return (
    <div css={[ITEM_STYLE, !enabled && ITEM_OFF_STYLE]}>
      <div css={HEADER_ROW_STYLE}>
        <div css={TEXT_BLOCK_STYLE}>
          <div css={[ITEM_TITLE_STYLE, !enabled && DISABLED_TEXT_STYLE]}>
            {baseProjectTypeLabel}
          </div>
          <div css={[ITEM_DESCRIPTION_STYLE, !enabled && DISABLED_TEXT_STYLE]}>
            {baseProjectTypeDescription}
          </div>
        </div>

        <div css={CONTROLS_STYLE}>
          <SettingsToggle
            checked={enabled}
            aria-label={`Enable ${baseProjectTypeLabel}`}
            onClick={onToggleClick}
          />
          <button
            aria-controls={bodyId}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? `Expand ${baseProjectTypeLabel}` : `Collapse ${baseProjectTypeLabel}`}
            css={[CHEVRON_STYLE, !isCollapsed && CHEVRON_EXPANDED_STYLE]}
            onClick={() => setIsCollapsed(collapsed => !collapsed)}
            type='button'
          >
            <DropdownIcon />
          </button>
        </div>
      </div>

      {isCollapsed ? null : (
        <div css={[BODY_STYLE, !enabled && BODY_OFF_STYLE]} id={bodyId}>
          <div css={INPUT_ROW_STYLE}>
            <div css={FIELD_WRAPPER_STYLE}>
              <CommonField
                label='Custom Project Label'
                style={FIELD_STYLE}
                state={enabled ? 'default' : 'disabled'}
                css={FIELD_PLACEHOLDER_STYLE}
                boxCss={enabled ? FIELD_BOX_STYLE : undefined}
                labelCss={FIELD_LABEL_STYLE}
                valueCss={[
                  FIELD_VALUE_STYLE,
                  { paddingRight: 72 },
                ]}
                inputProps={{
                  value: projectTypeLabel,
                  placeholder: 'Enter custom label',
                  maxLength: 20,
                  onChange: event => onProjectTypeLabelChange?.(event.target.value),
                  onBlur: () => onFieldBlur?.(),
                  onFocus: () => onFieldFocus?.(),
                }}
              />
              <CommonFieldAdornment
                css={[FIELD_COUNT_STYLE, FIELD_COUNT_WITH_ICON_STYLE]}
              >
                {`${projectTypeLabel.length}/20`}
              </CommonFieldAdornment>
              <CommonActionButton
                style={{
                  ...INFO_ICON_BUTTON_STYLE,
                  ...(!enabled ? INFO_ICON_BUTTON_DISABLED_STYLE : undefined),
                }}
                aria-label='Project type information'
                aria-describedby={showProjectLabelTooltip ? tooltipId : undefined}
                aria-expanded={showProjectLabelTooltip}
                onClick={() => setShowProjectLabelTooltip(current => !current)}
              >
                <InfoIcon />
              </CommonActionButton>
              {showProjectLabelTooltip
                ? (
                  <div id={tooltipId} role='tooltip' css={PROJECT_LABEL_TOOLTIP_STYLE}>
                    {PROJECT_LABEL_TOOLTIP_TEXT}
                  </div>
                )
                : null}
            </div>

            <div css={FIELD_WRAPPER_STYLE}>
              <CommonField
                label='Custom Project Description'
                style={FIELD_STYLE}
                state={enabled ? 'default' : 'disabled'}
                css={FIELD_PLACEHOLDER_STYLE}
                boxCss={enabled ? FIELD_BOX_STYLE : undefined}
                labelCss={FIELD_LABEL_STYLE}
                valueCss={FIELD_VALUE_STYLE}
                inputProps={{
                  value: projectDescription,
                  placeholder: 'Enter custom description',
                  maxLength: 100,
                  onChange: event => onProjectDescriptionChange?.(event.target.value),
                  onBlur: () => onFieldBlur?.(),
                  onFocus: () => onFieldFocus?.(),
                }}
              />
              <CommonFieldAdornment css={FIELD_COUNT_STYLE}>
                {`${projectDescription.length}/100`}
              </CommonFieldAdornment>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
